import React from 'react';
import cx from 'classnames';

import PageLayout from '@magoosh/layouts/page';
import { PageProps } from '@magoosh/layouts/types';
import { formatPrice } from 'utilities/money';

import SectionHeader from '@marketing/components/section_header';
import FAQSection from '@marketing/sections/faq';
import { OfferProps } from '@marketing/types';

import TestimonialsSection from './sections/testimonials';
import TopBusinessSchools from './sections/top_business_schools';

import CompetitorComparisonSection from '../sections/competitor_comparison';
import HeroSection from '../sections/hero';
import OffersSection from '../sections/offers';
import StartYourPrepSection from '../sections/start_your_prep';

import AdmissionsGuidanceIcon from '@marketing/sections/value_props/assets/green/timely_guidance_icon.svg';
import MathVerbalDataInsightsIcon from '@marketing/sections/value_props/assets/green/banner_check_icon.svg';
import PracticeQuestionsIcon from '@marketing/sections/value_props/assets/green/practice_questions_icon.svg';
import PracticeTestsIcon from '@marketing/sections/value_props/assets/green/practice_tests_icon.svg';
import ScoreIncreaseIcon from '@marketing/sections/value_props/assets/green/score_gauge_icon.svg';
import VideoLessonsIcon from '@marketing/sections/value_props/assets/green/video_lessons_icon.svg';

import BubbleQuestionIcon from '../sections/competitor_comparison/assets/bubble_question_icon.svg';
import ChecklistIcon from '../sections/competitor_comparison/assets/checklist_icon.svg';
import CircleRefreshIcon from '../sections/competitor_comparison/assets/circle_refresh_icon.svg';
import DollarSignIcon from '../sections/competitor_comparison/assets/dollar_sign_icon.svg';
import FireworkIcon from '../sections/competitor_comparison/assets/firework_icon.svg';
import FlashcardsIcon from '../sections/competitor_comparison/assets/flashcards_icon.svg';
import HighFiveIcon from '../sections/competitor_comparison/assets/high_five_icon.svg';
import HourglassIcon from '../sections/competitor_comparison/assets/hourglass_icon.svg';
import MobileDeviceIcon from '../sections/competitor_comparison/assets/mobile_device_icon.svg';
import NotebookIcon from '../sections/competitor_comparison/assets/notebook_icon.svg';
import ScoreGaugeIcon from '../sections/competitor_comparison/assets/score_gauge_icon.svg';
import StarIcon from '../sections/competitor_comparison/assets/star_icon.svg';
import VideoPlayerIcon from '../sections/competitor_comparison/assets/video_player_icon.svg';

import styles from '../style.module.scss';
import marketingStyles from '@marketing/style.module.scss';

interface Props extends PageProps {
  hideSignUp: boolean;
  isLoggedIn: boolean;
  offers: OfferProps[];
}

export default (props: Props) => {
  const admissionsBundleOffer = props.offers.find((offer) => offer.slug === 'premium-mba-admissions-bundle' || offer.slug === 'premium-mba-admissions-bundle-in');
  const mathVerbalOffer = props.offers.find((offer) => offer.slug === 'math-verbal' || offer.slug === 'math-verbal-in');
  const mathVerbal6MonthsOffer = props.offers.find((offer) => offer.slug === 'math-verbal-6' || offer.slug === 'math-verbal-6-in');
  const trialOffer = props.offers.find((offer) => offer.slug === '1-week');

  return (
    <div style={{ overflow: 'hidden', position: 'relative' }}>
      {/* Absolutely positioned background elements */}
      <>
        <div
          className={marketingStyles.greenGradientBg}
          style={{ position: 'absolute', height: 900, left: 0, top: 0, width: '100%' }}
        />
        <div className={styles.greenEllipse} />
      </>

      <PageLayout
        childrenStyle={{ paddingTop: 30 }}
        dashedBorder
        examName="GMAT"
        navigationItems={props.navigationItems}
        notice={props.notice}
      >
        <HeroSection
          examName="GMAT"
          header="Improve Your GMAT Focus Score, Guaranteed!"
          subHeader="Prep smart for the GMAT Focus for a fraction of the price of competitors"
          valueProps={[
            {
              description: 'High-quality material for each section of the test created by GMAT experts',
              icon: MathVerbalDataInsightsIcon,
              name: 'Math, Verbal, Data Insights'
            },
            {
              description: 'Time yourself on full-length practice tests, just like you would on test day',
              icon: PracticeTestsIcon,
              name: '2+ Practice Tests'
            },
            {
              description: 'Learn new skills and study hacks to improve your GMAT Focus score',
              icon: VideoLessonsIcon,
              name: '200+ Video Lessons'
            },
            {
              description: 'Increase your score by up to 70 points or your money back',
              icon: ScoreIncreaseIcon,
              name: 'Score Increase Guarantee'
            },
            {
              description: 'Learn from your mistakes with a video explanation to every question',
              icon: PracticeQuestionsIcon,
              name: '800+ Practice Questions'
            },
            {
              description: 'Insider advice on how to build your best MBA application',
              icon: AdmissionsGuidanceIcon,
              name: 'MBA Admissions Support'
            }
          ]}
        />

        <TopBusinessSchools />

        <OffersSection
          primaryOffer={{
            header: (
              <>
                <span className="text-bold text-white">Premium</span>
                {' '}<span style={{ color: '#8E70B2' }}>+</span>{' '}
                <span className="text-bold text-white">Admissions</span>
              </>
            ),
            highlight: <><small>Bundle and save <strong>$248</strong> ($697 value)</small></>,
            offer: admissionsBundleOffer,
            subHeader: 'Take your application to the next level',
            valuePropGroups: [
              {
                content: [
                  {
                    content: '12 months access to everything in our Premium GMAT plan',
                  }
                ],
                header: 'Premium GMAT ($498 value)'
              },
              {
                content: [
                  {
                    content: 'Application tracker',
                    tooltip: 'Manage your business school applications<br />in one central place.'
                  },
                  {
                    content: 'Insider advice on building an MBA brand, writing essays, and crafting a resume',
                    tooltip: 'Learn how to put your best foot forward<br />from a top lineup of Magoosh admissions experts<br />with a range of diverse backgrounds.'
                  },
                  {
                    content: 'Over 30 MBA application videos',
                    tooltip: 'Comprehensive lessons on the three critical<br />phases of the application process:<br />Prepare, Apply, Go'
                  },
                  {
                    content: 'Over 70 applicant profiles, essays, and resumes from real students',
                    tooltip: 'Check out actual student writings to help you<br /> tell your story in the best light'
                  },
                  {
                    content: 'Step-by-step guided essay writing',
                    tooltip: 'Go from blank page to well-written draft<br />with our Guided Writing experience.'
                  },
                  {
                    content: '12 months of access',
                    tooltip: 'Full access to Magoosh Admissions for 12 months.'
                  }
                ],
                header: 'Admissions Support ($199 value)',
                subHeader: 'Demystify MBA applications with people who have been there'
              }
            ]
          }}
          secondaryOffer={{
            header: (
              <>
                <span className="text-bold text-gray-darker">Premium</span>
                {' '}<span style={{ color: '#9BBCAF' }}>·</span>{' '}
                <span className="text-bold text-gray-darker">12 months</span>
              </>
            ),
            offer: mathVerbalOffer,
            subHeader: 'Save $149 by purchasing 12 months at once',
            valuePropGroups: [
              {
                content: [
                  {
                    content: <>Access to <strong>Math, Verbal, and Data Insights</strong></>,
                    tooltip: "This complete prep option gives you<br />content in every subject area"
                  },
                  {
                    content: <><strong>200+</strong> video lessons</>,
                    tooltip: 'Lessons cover every concept tested on the GMAT'
                  },
                  {
                    content: <><strong>Over 800</strong> practice questions</>,
                    tooltip: 'Every question has its own in-depth<br />explanation video'
                  },
                  {
                    content: <>At least <strong>2 diagnostic tests</strong></>,
                    tooltip: 'Our timed and scored full-length diagnostic<br />tests are generated from your pool of 800<br />practice questions. We recommend taking 2<br /> tests with Magoosh, but you can take more.'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: <><strong>GMAT Focus ready</strong></>,
                    tooltip: 'Questions, lessons, and practice tests<br />are all updated for the new GMAT Focus.'
                  },
                  {
                    content: <><strong>12 months</strong> of access</>,
                    tooltip: 'Full access for 12 months'
                  },
                  {
                    content: <><strong>Up to 70-point</strong> score guarantee</>,
                    tooltip: "If you've taken the test before and meet<br />our requirements, we guarantee you'll improve your<br />overall score or we'll give you a full refund. See<br />the Frequently Asked Questions below for details."
                  },
                  {
                    content: 'Ask an expert',
                    tooltip: 'Quick email and chat support for when you<br />get stuck or just want some extra help'
                  },
                  {
                    content: 'Score predictor',
                    tooltip: 'Based on data and official scores from<br />previous Magoosh students'
                  },
                  {
                    content: 'Pause your plan',
                    tooltip: "Need a break? Pause your plan until you're<br />ready to study again. If you're on installments,<br />payments are not paused and will continue as<br />scheduled, but your plan's end date will be<br />extended by the paused duration."
                  },
                  {
                    content: 'Switch to GRE',
                    tooltip: 'If you decide later that GRE is a better test<br />for you, we can switch your exam for free.<br />See FAQ for details.'
                  }
                ],
                header: 'Features'
              }
            ]
          }}
          tertiaryOffer={{
            header: (
              <>
                <span className="text-bold text-gray-darker">Premium</span>
                {' '}<span style={{ color: '#C6C6C6' }}>·</span>{' '}
                <span className="text-bold text-gray-darker">6 months</span>
              </>
            ),
            offer: mathVerbal6MonthsOffer,
            subHeader: 'Great option for limited study time',
            valuePropGroups: [
              {
                content: [
                  {
                    content: <>Access to <strong>Math, Verbal, and Data Insights</strong></>,
                    tooltip: "This complete prep option gives you<br />content in every subject area"
                  },
                  {
                    content: <><strong>200+</strong> video lessons</>,
                    tooltip: 'Lessons cover every concept tested on the GMAT'
                  },
                  {
                    content: <><strong>Over 800</strong> practice questions</>,
                    tooltip: 'Every question has its own in-depth<br />explanation video'
                  },
                  {
                    content: <>At least <strong>2 diagnostic tests</strong></>,
                    tooltip: 'Our timed and scored full-length diagnostic<br />tests are generated from your pool of 800<br />practice questions. We recommend taking 2<br /> tests with Magoosh, but you can take more.'
                  }
                ],
                header: 'Content'
              },
              {
                content: [
                  {
                    content: <><strong>GMAT Focus ready</strong></>,
                    tooltip: 'Questions, lessons, and practice tests<br />are all updated for the new GMAT Focus.'
                  },
                  {
                    content: <><strong>6 months</strong> of access</>,
                    tooltip: 'Full access for 6 months'
                  },
                  {
                    content: <><strong>Up to 70-point</strong> score guarantee</>,
                    tooltip: "If you've taken the test before and meet<br />our requirements, we guarantee you'll improve your<br />overall score or we'll give you a full refund. See<br />the Frequently Asked Questions below for details."
                  },
                  {
                    content: 'Ask an expert',
                    tooltip: 'Quick email and chat support for when you<br />get stuck or just want some extra help'
                  },
                  {
                    content: 'Score predictor',
                    tooltip: 'Based on data and official scores from<br />previous Magoosh students'
                  },
                  {
                    content: 'Pause your plan',
                    tooltip: "Need a break? Pause your plan until you're<br />ready to study again. If you're on installments,<br />payments are not paused and will continue as<br />scheduled, but your plan's end date will be<br />extended by the paused duration."
                  },
                  {
                    content: 'Switch to GRE',
                    tooltip: 'If you decide later that GRE is a better test<br />for you, we can switch your exam for free.<br />See FAQ for details.'
                  }
                ],
                header: 'Features'
              }
            ],
          }}
          trialOffer={{
            header: 'Try Magoosh GMAT for 7 days',
            offer: trialOffer,
            subHeader: 'Start a free trial with 20+ questions and lessons'
          }}
        />

        <CompetitorComparisonSection
          companyFeatureOfferings={[
            {
              name: 'Magoosh',
              'Price': formatPrice(mathVerbalOffer.fullPriceInCents, 'usd', true),
              'Length of Online Access': '12 months',
              'Practice Questions and Exams': 'Included',
              'Mobile App': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'Video & Text Question Explanations': 'Included',
              'No Questions Asked Money-Back Guarantee': 'Included',
              'Score-Improvement Guarantee': 'Up to 70-Point Increase Guarantee',
              'Study Plans': 'Included',
              'Flashcards Included': 'Included',
              'Score Predictor': 'Included',
              'Fully Sortable Question History': 'Included'
            },
            {
              name: 'Kaplan',
              'Price': '$1599',
              'Length of Online Access': '6 months',
              'Practice Questions and Exams': 'Included',
              'Mobile App': 'Included',
              'Video Lessons': 'Included',
              'Study Plans': 'Included',
              'Score Predictor': 'Included'
            },
            {
              name: 'Princeton Review',
              'Price': '$799',
              'Length of Online Access': '4 months',
              'Practice Questions and Exams': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'Video & Text Question Explanations': 'Included',
              'Score-Improvement Guarantee': 'Included',
              'Fully Sortable Question History': 'Included'
            },
            {
              name: 'Target Test Prep',
              'Price': '$649',
              'Length of Online Access': '4 months',
              'Practice Questions and Exams': 'Included',
              'Free Trial': 'Included',
              'Video Lessons': 'Included',
              'Video & Text Question Explanations': 'Included',
              'Score-Improvement Guarantee': 'Included',
              'Study Plans': 'Included',
              'Score Predictor': 'Included',
              'Fully Sortable Question History': 'Included'
            }
          ]}
          features={[
            { icon: DollarSignIcon, name: 'Price' },
            { icon: HourglassIcon, name: 'Length of Online Access' },
            { icon: BubbleQuestionIcon, name: 'Practice Questions and Exams' },
            { icon: MobileDeviceIcon, name: 'Mobile App' },
            { icon: FireworkIcon, name: 'Free Trial' },
            { icon: VideoPlayerIcon, name: 'Video Lessons' },
            { icon: NotebookIcon, name: 'Video & Text Question Explanations' },
            { icon: CircleRefreshIcon, name: 'No Questions Asked Money-Back Guarantee' },
            { icon: HighFiveIcon, name: 'Score-Improvement Guarantee' },
            { icon: ChecklistIcon, name: 'Study Plans' },
            { icon: FlashcardsIcon, name: 'Flashcards Included' },
            { icon: ScoreGaugeIcon, name: 'Score Predictor' },
            { icon: StarIcon, name: 'Fully Sortable Question History' }
          ]}
          registrationUrl={mathVerbalOffer.registrationUrl}
        />

        <TestimonialsSection />

        <FAQSection
          entries={[
             {
              answer: (
                <>
                  Yes! <a href="/gmat-focus-edition">The GMAT Focus officially replaced the original GMAT on Feb. 1, 2024</a>. Our video lessons, practice questions, and practice tests have been updated accordingly to reflect what you'll see on test day. We've also updated our study schedules.
                </>
              ),
              question: 'Can I use Magoosh to study for the GMAT Focus?'
            },
            {
              answer: "It's self-paced and all online. Access our materials from home or on-the-go from your phone.",
              question: 'How does the course work?'
            },
            {
              answer: (
                <>
                  We guarantee after completing our premium program, you'll see a <a href="/score-guarantee">point increase of up to 70 points over your old scores</a>, or we'll give you all of your money back. If your plan includes on-demand classes, you'll receive a refund of the premium portion of your plan only. We are unable to give refunds for on-demand classes.
                </>
              ),
              question: 'How does the score guarantee work?'
            },
            {
              answer: (
                <>
                  If you’re unhappy for any reason, email us within 7 days of the <i>original</i> purchase date (regardless if you pay in installments), and we will give you a full refund. No strings attached. If you purchase Magoosh using a non-US credit card and request a refund, sometimes your credit card company will keep the foreign currency transaction fee.
                </>
              ),
              question: 'How does the refund policy work?'
            },
            {
              answer: (
                <>
                  As long as you have an active GMAT Premium plan, you can switch to a GRE Premium plan for free. How long you've studied with Magoosh GMAT will determine how many months of access you'll have to <a href="https://gre.magoosh.com/plans">Magoosh GRE</a>. Reach out to us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> to switch and for the full details.
                </>
              ),
              question: 'How does switching my plan from GMAT to GRE work?'
            },
            {
              answer: 'All prices are in USD (United States Dollars).',
              question: 'What currency are the prices listed in?'
            },
            {
              answer: 'Visa, Mastercard, American Express, Discover, and PayPal. We process our transactions through Paypal, so your credit card info is safe.',
              question: 'What types of payment do you accept?'
            },
            {
              answer: (
                <>
                  <p>If you choose to pay in four monthly installments, your first payment is due at checkout, with the remaining installments charged monthly. You'll receive an email with your payment schedule after checkout. </p><br />
                  <p><strong>Please note:</strong> Installment plans are not subscriptions—payments continue even if you pause your plan, but your access will be extended by the paused duration. By choosing an installment plan, you are committing to paying the full purchase amount unless you request a refund within 7 days of your initial purchase. Pausing your plan does not extend the refund window.</p>
                </>
              ),
              question: 'How do monthly payments work?'
            },
            {
              answer: (
                <>
                  <p>Anyone can use Magoosh, but it's best for individuals who fall into one of the following categories:</p>
                  <ul>
                    <li><b>Self-studiers:</b> those who prefer to study on their own time and at their own pace.</li>
                    <li><b>Video-lovers:</b> those who learn better by watching video than through reading or live-learning. Every practice question has a video explanation.</li>
                    <li><b>Busy students and professionals:</b> those who do not have much time to study and want to sneak in a practice session here and there.</li>
                  </ul>
                </>
              ),
              question: 'Who should use Magoosh?'
            },
            {
              answer: "You get access to full-length practice tests that reflect what you'll see on test day. The tests are generated from your pool of unanswered questions so we recommend creating up to 2 practice tests and saving the remaining for practice.",
              question: 'Do I get practice tests?'
            },
            {
              answer: "Extremely close. Our curriculum developers have many years of experience teaching the GMAT and have extensive knowledge of what appears on the exam and what doesn't.",
              question: 'How close are the questions to actual GMAT Focus questions?'
            },
            {
              answer: "The material covers a wide range of difficulty from easy (below 400 on the old scoring scale) to hard (above 700 on the old scoring scale). We've designed the product to help you master the basics and progress towards your target score. For each question we provide the difficulty level.",
              question: 'How difficult are the questions?'
            },
            {
              answer: (
                <>
                  We’re happy to help you with questions you have about any of our material. You can use the "Help" tab on the Dashboard or email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> and we'll usually respond within 1-2 business days. We do have limitations to how much we can help any individual student, but in most cases (for 99% of students), we can answer all of your questions!
                </>
              ),
              question: "If I'm struggling with a question, can I get help?"
            },
            {
              answer: (
                <>
                  Check out this <a href="https://magoosh.zendesk.com/hc/en-us/articles/360048879351">help article with a list of accessibility features!</a>
                </>
              ),
              question: 'What accessibility features are there?'
            },
            {
              answer: (
                <>
                  Depending on your subscription, access lasts for either 6 months or 12 months. We also have a <a href="/register/premium-30-days">30 day plan</a>
                </>
              ),
              question: 'How long do I get access?'
            },
            {
              answer: (
                <>
                  You definitely can! Email us at <a href="mailto:help@magoosh.com">help@magoosh.com</a> and we'll help set you up.
                </>
              ),
              question: 'Can I purchase multiple accounts for my students or non-profit?'
            }
          ]}
        />

        <StartYourPrepSection
          examName="GMAT"
          pointIncrease="Up to 70 points"
          primaryOffer={{ ...admissionsBundleOffer, name: 'GMAT + Admissions' }}
          secondaryOffer={{ ...mathVerbalOffer, name: 'GMAT Only' }}
          trialOffer={trialOffer}
        />
      </PageLayout>
    </div>
  );
};
